import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  constructor() { }

  /**
   * Filters any array of data based on search query rulesets
   * @param data Array of objects to filter
   * @param query Array of rulesets from search component
   * @param fieldMappingConfig Configuration for field mappings per data type
   * @returns Filtered array
   */
  filterDataByQuery<T>(data: T[], query: any[], fieldMappingConfig: FieldMappingConfig): T[] {
    if (!query || query.length === 0) {
      return data;
    }


    const filteredData = data.filter(item => {
      // Each query item is a ruleset that needs to be evaluated
      const rulesetResults = query.map(ruleset => this.evaluateRuleset(item, ruleset, fieldMappingConfig));
      
      // All rulesets must be true (AND logic between rulesets)
      return rulesetResults.every(result => result);
    });

    
    return filteredData;
  }

  /**
   * Evaluates a single ruleset against a data item
   */
  private evaluateRuleset(item: any, ruleset: any, fieldMappingConfig: FieldMappingConfig): boolean {
    
    
    const { rules, selector } = ruleset;
    
    if (!rules || rules.length === 0) {
      return true;
    }

    const ruleResults = rules.map(rule => this.evaluateRule(item, rule, fieldMappingConfig));
    
    // Apply selector logic (and/or)
    if (selector === 'or') {
      return ruleResults.some(result => result);
    } else {
      // Default to 'and'
      return ruleResults.every(result => result);
    }
  }

  /**
   * Evaluates a single rule against a data item
   */
  private evaluateRule(item: any, rule: any, fieldMappingConfig: FieldMappingConfig): boolean {
    try {
      const { field, opr, value, type } = rule;
      
      if (!field || !opr) {
        console.warn('Invalid rule: missing field or operator', rule);
        return false;
      }
      
      const itemValue = this.getItemFieldValue(item, field, fieldMappingConfig);
      
      if (type === 'category') {
        // For category types, value should be an array
        const valueArray = Array.isArray(value) ? value : [value];
        const result = this.evaluateCategoryRule(itemValue, opr, valueArray);
        
        return result;
      } else {
        const result = this.evaluateFieldRule(itemValue, opr, value, type);
        
        return result;
      }
    } catch (error) {
      console.error('Error evaluating rule:', error, rule);
      return false;
    }
  }

  /**
   * Gets the value of a specific field from a data item
   */
  private getItemFieldValue(item: any, fieldName: string, fieldMappingConfig: FieldMappingConfig): any {
    const fieldMapping = fieldMappingConfig.getFieldMapping(item);
    
    const value = fieldMapping[fieldName];
    
    
    return value !== undefined ? value : '';
  }

  /**
   * Evaluates category-type rules (for tags, status, etc.)
   */
  private evaluateCategoryRule(itemValue: any, operator: string, values: string[]): boolean {
    if (!values || values.length === 0) {
      return true;
    }
    
    const itemValueStr = (itemValue || '').toString().toLowerCase();
    
    switch (operator) {
      case 'contains-all':
        return values.every(val => itemValueStr.includes(val.toLowerCase()));
      case 'contains-none':
        return !values.some(val => itemValueStr.includes(val.toLowerCase()));
      case 'contains-any':
        return values.some(val => itemValueStr.includes(val.toLowerCase()));
      default:
        return false;
    }
  }

  /**
   * Evaluates field-type rules (string, number, date, boolean)
   */
  private evaluateFieldRule(itemValue: any, operator: string, value: any, type: string): boolean {
    // Handle null/undefined values
    if (itemValue === null || itemValue === undefined) {
      itemValue = '';
    }

    // Type conversions
    if (type === 'boolean') {
      const itemBool = this.toBooleanValue(itemValue);
      const searchBool = this.toBooleanValue(value);
      return itemBool === searchBool;
    }

    if (type === 'number') {
      const itemNum = parseFloat(itemValue) || 0;
      const searchNum = parseFloat(value) || 0;
      
      switch (operator) {
        case 'equals':
          return itemNum === searchNum;
        case 'greater':
          return itemNum > searchNum;
        case 'less':
          return itemNum < searchNum;
        default:
          return false;
      }
    }

    if (type === 'date') {
      const itemDate = new Date(itemValue);
      const searchDate = new Date(value);
      
      switch (operator) {
        case 'equals':
          return itemDate.getTime() === searchDate.getTime();
        case 'greater':
          return itemDate.getTime() > searchDate.getTime();
        case 'less':
          return itemDate.getTime() < searchDate.getTime();
        default:
          return false;
      }
    }

    // String operations
    const itemStr = (itemValue || '').toString().toLowerCase();
    const searchStr = (value || '').toString().toLowerCase();

    switch (operator) {
      case 'equals':
        return itemStr === searchStr;
      case 'contains':
        return itemStr.includes(searchStr);
      case 'contains-none':
        return !itemStr.includes(searchStr);
      default:
        return false;
    }
  }

  /**
   * Converts various values to boolean
   */
  private toBooleanValue(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    const str = (value || '').toString().toLowerCase();
    return str === 'true' || str === '1';
  }

  /**
   * Helper method to extract values from arrays of objects
   */
  extractArrayValues(array: any[], fields: string[]): string {
    if (!Array.isArray(array) || array.length === 0) {
      return '';
    }
    
    return array.map(item => {
      for (const field of fields) {
        if (item[field]) {
          return item[field];
        }
      }
      return item.guid || item.id || '';
    }).join(',');
  }

  /**
   * Creates a field mapping configuration for projects
   */
  createProjectFieldMapping(): FieldMappingConfig {
    return new FieldMappingConfig((project: any) => ({
      'Title': project.title || project.name,
      'Description': project.description,
      'Due Date': project.due_date || project.internal_end_date,
      'Completed Date': project.completed_date,
      'Progress': project.progress,
      'Internal': project.internal,
      'Status': project.status,
      'Tags': this.extractArrayValues(project.tags, ['guid']),
      'Groups': this.extractArrayValues(project.groups, ['guid']),
    }));
  }

  /**
   * Creates a field mapping configuration for tasks
   */
  createTaskFieldMapping(): FieldMappingConfig {
    return new FieldMappingConfig((task: any) => ({
      'Title': task.title || task.name,
      'Description': task.description,
      'Due Date': task.due_date,
      'Completed Date': task.completed_date,
      'Progress': task.progress,
      'Internal': task.internal,
      'Status': task.status,
      'Tags': this.extractArrayValues(task.tags, ['guid']),
    }));
  }

  /**
   * Creates a field mapping configuration for clients
   */
  createClientFieldMapping(): FieldMappingConfig {
    return new FieldMappingConfig((client: any) => ({
      'Active': client.active,
      'Display Name': client.display_name,
      'Email': client.email,
      'Is Your Client': client.is_a_client,
      'Groups': this.extractArrayValues(client.groups, ['guid']),
      'Tags': this.extractArrayValues(client.tags, ['guid']),
      'Team Members': this.extractArrayValues(client.team_members, ['guid']),
    }));
  }

  /**
   * Creates a field mapping configuration for companies
   */
  createCompanyFieldMapping(): FieldMappingConfig {
    return new FieldMappingConfig((company: any) => ({
      'Display Name': company.display_name,
      'Company Name': company.name,
      'Groups': this.extractArrayValues(company.groups, ['guid']),
      'Tags': this.extractArrayValues(company.tags, ['guid']),
      'Team Members': this.extractArrayValues(company.team_members, ['guid']),
    }));
  }
}

/**
 * Configuration class for field mappings
 */
export class FieldMappingConfig {
  constructor(private mappingFunction: (item: any) => { [key: string]: any }) {}

  getFieldMapping(item: any): { [key: string]: any } {
    return this.mappingFunction(item);
  }
}
